/**
 * Servicio para manejar verificaciones automáticas de versión
 * Permite verificar periódicamente si hay nuevas versiones de la agenda
 */

import { Storage } from '@capacitor/storage';
import { checkVersionUpdate, checkAndUpdateData } from '../data/dataApi';

export interface VersionCheckConfig {
  /** Intervalo en minutos para verificar versión automáticamente */
  checkIntervalMinutes: number;
  /** Si debe mostrar notificaciones al usuario */
  showNotifications: boolean;
  /** Si debe actualizar automáticamente sin preguntar */
  autoUpdate: boolean;
}

export class VersionCheckService {
  private static instance: VersionCheckService;
  private checkInterval: NodeJS.Timeout | null = null;
  private isChecking = false;
  private config: VersionCheckConfig = {
    checkIntervalMinutes: 30, // Verificar cada 30 minutos por defecto
    showNotifications: true,
    autoUpdate: false
  };

  static getInstance(): VersionCheckService {
    if (!VersionCheckService.instance) {
      VersionCheckService.instance = new VersionCheckService();
    }
    return VersionCheckService.instance;
  }

  /**
   * Configura el servicio de verificación de versión
   */
  configure(config: Partial<VersionCheckConfig>): void {
    this.config = { ...this.config, ...config };
    console.log('🔧 VersionCheckService configurado:', this.config);
  }

  /**
   * Inicia las verificaciones automáticas de versión
   */
  async startAutoCheck(): Promise<void> {
    try {
      // Obtener código de evento
      const { value: eventCode } = await Storage.get({ key: 'eventcode' });
      
      if (!eventCode || eventCode === 'nada') {
        console.log('⚠️ No hay código de evento, no se puede iniciar verificación automática');
        return;
      }

      // Detener verificación anterior si existe
      this.stopAutoCheck();

      console.log(`🚀 Iniciando verificación automática cada ${this.config.checkIntervalMinutes} minutos`);

      // Verificar inmediatamente
      await this.performVersionCheck(eventCode);

      // Configurar verificación periódica
      this.checkInterval = setInterval(async () => {
        await this.performVersionCheck(eventCode);
      }, this.config.checkIntervalMinutes * 60 * 1000);

    } catch (error) {
      console.error('❌ Error al iniciar verificación automática:', error);
    }
  }

  /**
   * Detiene las verificaciones automáticas
   */
  stopAutoCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('🛑 Verificación automática detenida');
    }
  }

  /**
   * Verifica manualmente si hay actualizaciones
   */
  async checkNow(): Promise<boolean> {
    try {
      const { value: eventCode } = await Storage.get({ key: 'eventcode' });
      
      if (!eventCode || eventCode === 'nada') {
        console.log('⚠️ No hay código de evento para verificar');
        return false;
      }

      return await this.performVersionCheck(eventCode, true);
    } catch (error) {
      console.error('❌ Error en verificación manual:', error);
      return false;
    }
  }

  /**
   * Realiza la verificación de versión
   */
  private async performVersionCheck(eventCode: string, isManual: boolean = false): Promise<boolean> {
    if (this.isChecking && !isManual) {
      console.log('⏳ Ya hay una verificación en progreso');
      return false;
    }

    this.isChecking = true;

    try {
      console.log('🔍 Verificando versión...');
      
      const versionCheck = await checkVersionUpdate(eventCode);
      
      if (!versionCheck.hasUpdate) {
        console.log('✅ Versión actualizada');
        return false;
      }

      console.log(`🆕 Nueva versión detectada: ${versionCheck.currentVersion} → ${versionCheck.serverVersion}`);

      // Si es verificación automática y está configurado para auto-actualizar
      if (!isManual && this.config.autoUpdate) {
        console.log('🔄 Actualizando automáticamente...');
        return await checkAndUpdateData(eventCode, true);
      }

      // Si debe mostrar notificaciones
      if (this.config.showNotifications) {
        this.showUpdateNotification(versionCheck.serverVersion, isManual);
      }

      return true;

    } catch (error) {
      console.error('❌ Error en verificación de versión:', error);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Muestra notificación de actualización disponible
   */
  private showUpdateNotification(newVersion: string, isManual: boolean): void {
    // Crear evento personalizado para notificar a la aplicación
    const event = new CustomEvent('versionUpdateAvailable', {
      detail: {
        newVersion,
        isManual
      }
    });
    
    window.dispatchEvent(event);

    // También mostrar en consola
    console.log(`📱 Nueva versión disponible: v${newVersion}`);
  }

  /**
   * Verifica versión cuando la app vuelve al foco
   */
  setupFocusCheck(): void {
    document.addEventListener('visibilitychange', async () => {
      if (!document.hidden) {
        console.log('👁️ App enfocada, verificando versión...');
        await this.checkNow();
      }
    });

    // También verificar cuando la ventana recibe foco
    window.addEventListener('focus', async () => {
      console.log('🎯 Ventana enfocada, verificando versión...');
      await this.checkNow();
    });
  }

  /**
   * Obtiene el estado actual del servicio
   */
  getStatus(): {
    isRunning: boolean;
    isChecking: boolean;
    config: VersionCheckConfig;
  } {
    return {
      isRunning: this.checkInterval !== null,
      isChecking: this.isChecking,
      config: this.config
    };
  }
}

// Exportar instancia singleton
export const versionCheckService = VersionCheckService.getInstance();

// Tipos para eventos personalizados
declare global {
  interface WindowEventMap {
    'versionUpdateAvailable': CustomEvent<{
      newVersion: string;
      isManual: boolean;
    }>;
  }
}
